import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import translations from './translations.json';

export const LANGUAGES = ['fr', 'en'];
export const DEFAULT_LANGUAGE = 'en';

const STORAGE_KEY = 'excel-maestro-language';

/**
 * The language to start in.
 *
 * An explicit choice wins over the browser's preference — the same trap the
 * dark-mode toggle had, where `||` could not tell "never chosen" from
 * "chosen and it happens to be the default". localStorage throws in some
 * privacy modes, so every access is guarded.
 */
export function detectLanguage(storage, navigatorLanguage) {
  try {
    const stored = storage?.getItem(STORAGE_KEY);
    if (LANGUAGES.includes(stored)) return stored;
  } catch (error) {
    // Storage unavailable (private browsing, blocked cookies): fall through.
  }

  const browser = String(navigatorLanguage || '').slice(0, 2).toLowerCase();
  return LANGUAGES.includes(browser) ? browser : DEFAULT_LANGUAGE;
}

/**
 * Look up one key, filling {placeholders} from params.
 *
 * A missing key falls back to English, then to the key itself — a screen with
 * "history.title" written on it is ugly, but it is visible, which is the point:
 * a silent empty string would ship unnoticed.
 */
export function translate(key, language, params) {
  const entry = translations[key];
  if (!entry) return key;

  const template = entry[language] ?? entry[DEFAULT_LANGUAGE] ?? key;
  if (!params) return template;

  return Object.keys(params).reduce(
    (text, name) => text.split(`{${name}}`).join(String(params[name])),
    template
  );
}

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() =>
    detectLanguage(
      typeof window === 'undefined' ? null : window.localStorage,
      typeof navigator === 'undefined' ? '' : navigator.language
    )
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, language);
    } catch (error) {
      // Not being able to remember the choice must not break the page.
    }
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((next) => {
    if (LANGUAGES.includes(next)) setLanguageState(next);
  }, []);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t: (key, params) => translate(key, language, params),
    }),
    [language, setLanguage]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}

/** Shorthand for the common case: `const t = useT();` */
export function useT() {
  return useContext(LanguageContext).t;
}
